import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, Sparkles } from "lucide-react";
import { useLocation } from "react-router-dom";

import { ProfileOverviewHeader } from "@/components/panel/ProfileOverviewHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useSupabaseSession } from "@/hooks/use-supabase-session";
import { useToast } from "@/hooks/use-toast";
import { getDailyGuidance } from "@/lib/karma/daily";
import { computeKarmaNumerology, parseBirthDate } from "@/lib/karma/numerology";
import { getSupabaseConfig, getSupabaseStorageKeyFromDirectUrl } from "@/lib/supabase/client";

type MeditationSession = {
  id: string;
  title: string;
  description: string;
  durationMinutes: number;
  level: "beginner" | "intermediate" | "advanced";
  coverImageUrl?: string;
  audioUrl?: string;
  active: boolean;
  tags: string[];
};

type MeditationsResponse = {
  ok: true;
  items: MeditationSession[];
};

type ContentMode = "meditation" | "breath";

function readAccessTokenFromStorage() {
  try {
    const { directUrl } = getSupabaseConfig();
    const storageKey = getSupabaseStorageKeyFromDirectUrl(String(directUrl)) ?? "";
    if (!storageKey) return "";
    const raw = globalThis.localStorage?.getItem(storageKey) ?? "";
    const parsed = raw ? (JSON.parse(raw) as any) : null;
    return typeof parsed?.access_token === "string" ? parsed.access_token : "";
  } catch {
    return "";
  }
}

async function fetchMembershipSummary() {
  const token = readAccessTokenFromStorage();
  if (!token) return null;

  const res = await fetch("/api/numeroloji/me/entitlements", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) return null;
  const payload = (await res.json().catch(() => null)) as any;
  return payload?.membership ?? null;
}

async function fetchMeditations(mode: ContentMode): Promise<MeditationSession[]> {
  const endpoint = mode === "breath" ? "/api/numeroloji/breaths" : "/api/numeroloji/meditations";
  const res = await fetch(endpoint);
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    const fallback = mode === "breath" ? "Nefes seansları alınamadı." : "Meditasyonlar alınamadı.";
    throw new Error(typeof json?.error === "string" ? json.error : fallback);
  }
  const data = json as MeditationsResponse;
  return Array.isArray(data?.items) ? data.items : [];
}

function normalizeTag(value: string) {
  return value
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/\s+/g, "")
    .replace(/[ç]/g, "c")
    .replace(/[ğ]/g, "g")
    .replace(/[ı]/g, "i")
    .replace(/[ö]/g, "o")
    .replace(/[ş]/g, "s")
    .replace(/[ü]/g, "u")
    .replace(/[^0-9a-z]+/g, "");
}

function getKarmicDebtTag(code: number) {
  return `karmikborc${code}`;
}

function levelLabel(level: MeditationSession["level"]) {
  if (level === "advanced") return "İleri";
  if (level === "intermediate") return "Orta";
  return "Başlangıç";
}

function formatDuration(durationMinutes: number, options?: { unitCase?: "lower" | "upper" }) {
  const totalSeconds = Math.max(0, Math.round(Math.max(0, Number(durationMinutes) || 0) * 60));
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;

  if (secs === 0) {
    const unit = options?.unitCase === "upper" ? "DK" : "dk";
    return `${mins} ${unit}`;
  }

  return `${mins}:${String(secs).padStart(2, "0")}`;
}

function toPublicUrl(rawValue?: string) {
  const raw = String(rawValue ?? "").trim();
  if (!raw) return "";
  if (raw.startsWith("data:") || raw.startsWith("blob:") || raw.startsWith("/")) return raw;
  if (raw.startsWith("//")) return `https:${raw}`;
  if (raw.startsWith("ftp://")) return raw.replace(/^ftp:\/\//i, "https://");
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  return `https://${raw}`;
}

const PROGRAM_DAYS_TAG_PREFIX = "program_gun:";
const PROGRAM_DAY_TRACKING_KEY = "numeroloji_program_day_tracking_v1";

type ProgramDayTrackingState = {
  startedAtByItemId: Record<string, string>;
};

type ProgramDayTrackingStore = Record<string, ProgramDayTrackingState>;

function formatLocalDateKey(d: Date) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseProgramDaysFromTags(tags: string[]) {
  const raw = (tags ?? [])
    .find((tag) => String(tag ?? "").startsWith(PROGRAM_DAYS_TAG_PREFIX))
    ?.slice(PROGRAM_DAYS_TAG_PREFIX.length) ?? "";

  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : 0;
}

function loadProgramDayTracking(userKey: string): ProgramDayTrackingState {
  if (typeof window === "undefined") return { startedAtByItemId: {} };

  try {
    const raw = window.localStorage.getItem(PROGRAM_DAY_TRACKING_KEY);
    if (!raw) return { startedAtByItemId: {} };
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return { startedAtByItemId: {} };

    const store = parsed as ProgramDayTrackingStore;
    const state = store[userKey];
    if (!state || typeof state !== "object") return { startedAtByItemId: {} };

    return {
      startedAtByItemId:
        state.startedAtByItemId && typeof state.startedAtByItemId === "object"
          ? (state.startedAtByItemId as Record<string, string>)
          : {},
    };
  } catch {
    return { startedAtByItemId: {} };
  }
}

function saveProgramDayTracking(userKey: string, state: ProgramDayTrackingState) {
  if (typeof window === "undefined") return;

  try {
    const raw = window.localStorage.getItem(PROGRAM_DAY_TRACKING_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    const base = parsed && typeof parsed === "object" ? (parsed as ProgramDayTrackingStore) : {};

    const next: ProgramDayTrackingStore = {
      ...base,
      [userKey]: state,
    };

    window.localStorage.setItem(PROGRAM_DAY_TRACKING_KEY, JSON.stringify(next));
  } catch {
    // ignore local storage errors
  }
}

function resolveProgramDay(startedDateKey: string | undefined, todayKey: string) {
  if (!startedDateKey) return 0;
  const start = new Date(`${startedDateKey}T00:00:00`);
  const today = new Date(`${todayKey}T00:00:00`);
  const startMs = start.getTime();
  const todayMs = today.getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(todayMs) || todayMs < startMs) return 1;
  return Math.floor((todayMs - startMs) / 86_400_000) + 1;
}

function resolveProgramDaysForSession(item: MeditationSession | null) {
  if (!item) return 0;

  const fromTags = parseProgramDaysFromTags(item.tags ?? []);
  if (fromTags > 0) return fromTags;

  const text = `${item.title} ${item.description} ${(item.tags ?? []).join(" ")}`;
  const match = text.match(/(\d{1,3})\s*g[uü]n/i);
  const parsed = Number(match?.[1] ?? "");
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : 0;
}


export default function UserPanelMeditasyon({
  mode = "meditation",
  forcedTag,
}: {
  mode?: ContentMode;
  forcedTag?: string;
} = {}) {
  const { user } = useSupabaseSession();
  const { toast } = useToast();
  const location = useLocation();
  const [query, setQuery] = useState("");

  const userKey = user?.id ? `sb:${user.id}` : "anon";
  const todayKey = useMemo(() => formatLocalDateKey(new Date()), []);
  const [programDayTracking, setProgramDayTracking] = useState<ProgramDayTrackingState>(() => loadProgramDayTracking(userKey));

  const isBreathMode = mode === "breath";
  const pageTitle = isBreathMode ? "Nefes Merkezi" : "Meditasyon Merkezi";
  const searchPlaceholder = isBreathMode ? "Nefes seansı ara..." : "Meditasyon ara...";
  const listTitle = isBreathMode ? "Haftalık Nefes Planınız" : "Haftalık Meditasyon Planınız";

  const urlTagFilter = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    const raw = sp.get("tag") ?? forcedTag ?? "";
    if (!raw) return null;
    const normalized = normalizeTag(raw);
    return normalized || null;
  }, [forcedTag, location.search]);

  const profileInput = useMemo(() => {
    const firstName = typeof user?.metadata?.first_name === "string" ? (user?.metadata?.first_name as string) : "";
    const lastName = typeof user?.metadata?.last_name === "string" ? (user?.metadata?.last_name as string) : "";
    const fullName = `${firstName} ${lastName}`.trim();

    const birthDay = typeof user?.metadata?.birth_day === "number" ? user?.metadata?.birth_day : Number(user?.metadata?.birth_day);
    const birthMonth =
      typeof user?.metadata?.birth_month === "number" ? user?.metadata?.birth_month : Number(user?.metadata?.birth_month);
    const birthYear = typeof user?.metadata?.birth_year === "number" ? user?.metadata?.birth_year : Number(user?.metadata?.birth_year);

    if (!fullName || !Number.isFinite(birthDay) || !Number.isFinite(birthMonth) || !Number.isFinite(birthYear)) return null;

    const birthDate = parseBirthDate({
      day: birthDay,
      month: birthMonth,
      year: birthYear,
    });

    return {
      fullName,
      birthDate,
    };
  }, [user?.metadata?.birth_day, user?.metadata?.birth_month, user?.metadata?.birth_year, user?.metadata?.first_name, user?.metadata?.last_name]);

  const numerology = useMemo(() => {
    if (!profileInput) return null;
    return computeKarmaNumerology(profileInput.fullName, profileInput.birthDate);
  }, [profileInput]);

  const karmicCodes = numerology?.karmicCodes ?? [];

  const daily = useMemo(() => getDailyGuidance(new Date()), []);

  const meditationsQuery = useQuery({
    queryKey: [isBreathMode ? "panel-breaths" : "panel-meditations"],
    queryFn: () => fetchMeditations(mode),
    staleTime: 30_000,
  });

  const entitlementsQuery = useQuery({
    queryKey: ["numeroloji-membership-summary", user?.id],
    enabled: Boolean(user?.id),
    staleTime: 30_000,
    retry: false,
    queryFn: fetchMembershipSummary,
  });

  const membershipHasFeature = useMemo(() => {
    const raw = entitlementsQuery.data?.hasFeature;
    const fromApi = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
    return Object.fromEntries(Object.entries(fromApi).map(([key, value]) => [key, Boolean(value)])) as Record<string, boolean>;
  }, [entitlementsQuery.data?.hasFeature]);

  const shown = useMemo(() => {
    const items = (meditationsQuery.data ?? []).filter((m) => m && m.active);

    const normalizedGeneralKeys = new Set(["genel", "general"].map(normalizeTag));
    const wantedKeys = karmicCodes.map((code) => normalizeTag(getKarmicDebtTag(code))).filter(Boolean);

    const personalized: MeditationSession[] = [];
    const general: MeditationSession[] = [];

    for (const m of items) {
      const tags = (m.tags ?? []).filter(Boolean);
      const tagKeys = tags.map(normalizeTag);
      const hasWanted = wantedKeys.length ? wantedKeys.some((w) => tagKeys.includes(w)) : false;
      const hasGeneral = tagKeys.some((tag) => normalizedGeneralKeys.has(tag));
      const isUntagged = tags.length === 0;

      if (hasWanted) {
        personalized.push(m);
      }

      if (hasGeneral || isUntagged) {
        general.push(m);
      }
    }

    const base = isBreathMode
      ? items
      : wantedKeys.length
        ? (personalized.length ? personalized : general.length ? general : items)
        : general.length ? general : items;

    let filtered = base;

    if (urlTagFilter) {
      filtered = filtered.filter((m) => (m.tags ?? []).filter(Boolean).map(normalizeTag).includes(urlTagFilter));
    }

    const q = query.trim().toLocaleLowerCase("tr-TR");
    const searched = !q
      ? filtered
      : filtered.filter((m) => {
        const hay = `${m.title} ${m.description} ${(m.tags ?? []).join(" ")}`.toLocaleLowerCase("tr-TR");
        return hay.includes(q);
      });

    const deduped = new Map<string, MeditationSession>();
    for (const item of searched) {
      const key = `${normalizeTag(item.title)}::${Math.max(1, Math.round(Number(item.durationMinutes || 0) * 60))}`;
      if (!deduped.has(key)) {
        deduped.set(key, item);
      }
    }

    return Array.from(deduped.values());
  }, [isBreathMode, karmicCodes, meditationsQuery.data, query, urlTagFilter]);

  const hasFullWellnessLibrary = Boolean(membershipHasFeature.full_wellness_library);
  const visibleSessions = hasFullWellnessLibrary ? shown : shown.slice(0, 3);
  const lockedSessionCount = Math.max(0, shown.length - visibleSessions.length);

  const hero = shown[0] ?? null;

  useEffect(() => {
    setProgramDayTracking(loadProgramDayTracking(userKey));
  }, [userKey]);

  const ensureProgramStart = useCallback(
    (itemId: string) => {
      setProgramDayTracking((prev) => {
        if (prev.startedAtByItemId[itemId]) return prev;
        const next = {
          startedAtByItemId: {
            ...prev.startedAtByItemId,
            [itemId]: todayKey,
          },
        };
        saveProgramDayTracking(userKey, next);
        return next;
      });
    },
    [todayKey, userKey],
  );

  const startProgram = useCallback(
    (item: MeditationSession) => {
      const programDays = resolveProgramDaysForSession(item);

      let startedNow = false;
      setProgramDayTracking((prev) => {
        if (prev.startedAtByItemId[item.id]) return prev;
        startedNow = true;
        const next = {
          startedAtByItemId: {
            ...prev.startedAtByItemId,
            [item.id]: todayKey,
          },
        };
        saveProgramDayTracking(userKey, next);
        return next;
      });

      if (startedNow) {
        toast({
          title: "Program başlatıldı",
          description: programDays > 0 ? `${programDays} günlük program Aktivite akışına eklendi.` : "Program Aktivite akışına eklendi.",
        });
      }
    },
    [todayKey, toast, userKey],
  );

  const cancelProgram = useCallback(
    (item: MeditationSession) => {
      setProgramDayTracking((prev) => {
        if (!prev.startedAtByItemId[item.id]) return prev;
        const nextStartedAt = { ...prev.startedAtByItemId };
        delete nextStartedAt[item.id];
        const next = { startedAtByItemId: nextStartedAt };
        saveProgramDayTracking(userKey, next);
        return next;
      });

      toast({
        title: "Program iptal edildi",
        description: `${item.title} programını tekrar başlatabilirsiniz.`,
      });
    },
    [toast, userKey],
  );


  return (
    <div className="relative z-0 mx-auto w-full max-w-6xl space-y-10">
      <ProfileOverviewHeader />
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="text-3xl font-black tracking-tight text-slate-900 dark:text-white">{pageTitle}</div>
          <div className="mt-2 max-w-2xl text-sm text-slate-600 dark:text-slate-400">
            Bugünün sayısı: <span className="font-bold text-[var(--panel-primary)]">{daily.number}</span> • {daily.focus}
            {urlTagFilter ? (
              <span className="ml-2 inline-flex items-center gap-2 rounded-full bg-[var(--panel-primary)]/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-[var(--panel-primary)]">
                Filtre: {urlTagFilter}
              </span>
            ) : null}
          </div>
        </div>

        <div className="w-full max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="h-11 rounded-2xl border-transparent bg-white pl-10 shadow-md ring-1 ring-slate-900/10 dark:bg-slate-900/50 dark:ring-white/10"
            />
          </div>
        </div>
      </div>

      <section>
        <div className="relative overflow-hidden rounded-3xl border border-[#d9c7d8] bg-[#f3e6f0] text-slate-900 ring-1 ring-[#d9c7d8]/60 shadow-[0_24px_70px_-30px_rgba(15,23,42,0.25)]">
          {hero?.coverImageUrl ? (
            <div className="absolute inset-0">
              <img src={toPublicUrl(hero.coverImageUrl)} alt="" className="h-full w-full object-cover opacity-28" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#f3e6f0] via-[#f3e6f0]/92 to-[#f3e6f0]/82" />
            </div>
          ) : (
            <div className="absolute inset-0 [background:radial-gradient(circle_at_30%_30%,rgba(168,122,162,0.30)_0%,transparent_55%),linear-gradient(145deg,#f6ebf4,#efdeec)]" />
          )}

          <div className="relative z-10 flex min-h-[320px] items-center px-6 py-10 sm:px-10">
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white/80 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-900 backdrop-blur">
                <Sparkles className="h-4 w-4" />
                Günün önerisi
              </div>

              <div className="mt-5 text-4xl font-extrabold leading-tight">
                {hero ? hero.title : daily.title}
              </div>
              <div className="mt-3 text-sm leading-relaxed text-slate-700">
                {hero ? hero.description : daily.meditation}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-slate-800">
                {hero ? (
                  <>
                    <div className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white/85 px-3 py-1 text-slate-900 backdrop-blur">
                      {formatDuration(hero.durationMinutes)}
                    </div>
                    <div className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white/85 px-3 py-1 text-slate-900 backdrop-blur">
                      {levelLabel(hero.level)}
                    </div>
                    <div className="inline-flex items-center gap-2 rounded-full border border-[var(--panel-primary)]/40 bg-[var(--panel-primary)]/10 px-3 py-1 font-black text-[var(--panel-primary)]">
                      {programDayTracking.startedAtByItemId[hero.id]
                        ? `Program: ${resolveProgramDay(programDayTracking.startedAtByItemId[hero.id], todayKey)}/${Math.max(1, resolveProgramDaysForSession(hero))}. gün`
                        : `Program hedefi: ${Math.max(1, resolveProgramDaysForSession(hero))} gün`}
                    </div>
                  </>
                ) : null}
              </div>

              {hero ? (
                <div className="mt-6 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => startProgram(hero)}
                    className="inline-flex items-center gap-2 rounded-2xl border border-[var(--panel-primary)]/30 bg-[var(--panel-primary)]/10 px-5 py-2 text-xs font-black text-[var(--panel-primary)]"
                  >
                    {programDayTracking.startedAtByItemId[hero.id] ? "Program devam ediyor" : "Programı başlat"}
                  </button>

                  {programDayTracking.startedAtByItemId[hero.id] ? (
                    <button
                      type="button"
                      onClick={() => {
                        const confirmed = window.confirm("Programı iptal etmek istediğinize emin misiniz?");
                        if (!confirmed) return;
                        cancelProgram(hero);
                      }}
                      className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-2 text-xs font-black text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300"
                    >
                      Programı iptal et
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>


      <section className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="bg-white p-6 shadow-lg ring-1 ring-slate-900/10 dark:bg-slate-900/40 dark:ring-white/10 lg:col-span-2">
          <div className="flex items-center gap-2 text-base font-bold text-slate-900 dark:text-white">
            <Sparkles className="h-4 w-4 text-[var(--panel-primary)]" />
            Neden Bu Öneriler?
          </div>
          <div className="mt-4 text-sm leading-relaxed text-slate-600 dark:text-slate-400">{daily.action}</div>
          <div className="mt-3 rounded-2xl bg-[var(--panel-primary)]/10 p-4 text-xs font-semibold text-[var(--panel-primary)]">
            Affirmation: {daily.affirmation}
          </div>
        </Card>

        <Card className="bg-white p-6 shadow-lg ring-1 ring-slate-900/10 dark:bg-slate-900/40 dark:ring-white/10">
          <div className="text-base font-bold text-slate-900 dark:text-white">Kişisel Etiketler</div>
          <div className="mt-3 text-sm text-slate-600 dark:text-slate-400">
            {karmicCodes.length ? (
              <div>Karmik Borç: {karmicCodes.join(", ")}</div>
            ) : (
              <div>Karmik borç bulunamadı (genel öneriler).</div>
            )}
          </div>
        </Card>
      </section>

      <section>
        <div className="mb-5 flex items-end justify-between gap-4">
          <div>
            <div className="text-xl font-extrabold text-slate-900 dark:text-white">{listTitle}</div>
            <div className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              Size uygun etiketlere göre listelenir.
            </div>
          </div>
        </div>

        {meditationsQuery.isLoading ? (
          <div className="text-sm text-slate-500 dark:text-slate-400">{isBreathMode ? "Nefes seansları yükleniyor..." : "Meditasyonlar yükleniyor..."}</div>
        ) : meditationsQuery.isError ? (
          <div className="text-sm text-red-600">{meditationsQuery.error instanceof Error ? meditationsQuery.error.message : "Yüklenemedi."}</div>
        ) : visibleSessions.length ? (
          <>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {visibleSessions.slice(0, 9).map((m) => {
              const programDays = Math.max(1, resolveProgramDaysForSession(m));
              const programStarted = Boolean(programDayTracking.startedAtByItemId[m.id]);
              const coverImageUrl = toPublicUrl(m.coverImageUrl);

              return (
              <div
                key={m.id}
                className="group flex flex-col overflow-hidden rounded-2xl border border-[#d9c7d8] bg-[#f3e6f0] p-4 shadow-sm ring-1 ring-[#d9c7d8]/60 transition-all hover:-translate-y-0.5 hover:shadow-lg"
              >
                <div className="relative mb-4 h-40 overflow-hidden rounded-xl bg-[#ead7e6]">
                  {coverImageUrl ? (
                    <img src={coverImageUrl} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  ) : (
                    <div className="h-full w-full [background:radial-gradient(circle_at_30%_30%,rgba(55,19,236,0.30)_0%,transparent_55%),linear-gradient(145deg,#141428,#0a0a0c)]" />
                  )}
                  <div className="absolute left-3 top-3 rounded border border-[#d0bdcc] bg-[#f3e6f0]/95 px-2 py-1 text-[10px] font-bold text-slate-900 backdrop-blur">
                    {formatDuration(m.durationMinutes, { unitCase: "upper" })}
                  </div>
                </div>

                <div className="text-sm font-bold text-slate-900">{m.title}</div>
                <div className="mt-1 line-clamp-2 text-xs text-slate-700">{m.description}</div>

                <div className="mt-4 flex items-center justify-between gap-2">
                  <span className="rounded-full border border-[#d0bdcc] bg-[#ead7e6] px-2 py-1 text-[10px] font-semibold text-slate-900">
                    {levelLabel(m.level)}
                  </span>
                  {programDays > 0 ? (
                    <span className="rounded-full bg-[var(--panel-primary)]/10 px-2 py-1 text-[10px] font-black text-[var(--panel-primary)]">
                      {programStarted ? `${resolveProgramDay(programDayTracking.startedAtByItemId[m.id], todayKey)}/${programDays}. gün` : `${programDays} gün`}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => startProgram(m)}
                    className="rounded-full border border-[var(--panel-primary)]/30 bg-[var(--panel-primary)]/10 px-3 py-1 text-[10px] font-black text-[var(--panel-primary)]"
                  >
                    {programStarted ? "Program devam ediyor" : "Programı başlat"}
                  </button>

                  {programStarted ? (
                    <button
                      type="button"
                      onClick={() => {
                        const confirmed = window.confirm("Programı iptal etmek istediğinize emin misiniz?");
                        if (!confirmed) return;
                        cancelProgram(m);
                      }}
                      className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-[10px] font-black text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300"
                    >
                      İptal
                    </button>
                  ) : null}
                </div>

              </div>
            );
            })}
            </div>
            {lockedSessionCount > 0 ? (
              <Card className="mt-5 border-[var(--panel-primary)]/25 bg-[var(--panel-primary)]/8 p-5 text-sm text-slate-700 dark:text-slate-200">
                Bu alandaki +{lockedSessionCount} içerik Plus/Pro üyelikte açılır.
              </Card>
            ) : null}
          </>
        ) : (
          <Card className="bg-white p-6 text-sm text-slate-600 shadow-md ring-1 ring-slate-900/10 dark:bg-slate-900/40 dark:text-slate-300 dark:ring-white/10">
            {isBreathMode
              ? "Eşleşen nefes seansı bulunamadı. Yönetim panelinden etiketlere “nefes” ekleyerek yayınlayabilirsiniz."
              : "Eşleşen meditasyon bulunamadı. Ürün yönetiminde meditasyon etiketlerini “Karmik Borç 1” veya “genel” olarak ekleyebilirsiniz."}
          </Card>
        )}
      </section>

    </div>
  );
}
